import { useState } from 'react';
import {
  HttpProvider,
  formatMoney,
  itemsTotal,
  structureReceipt,
  validateReceipt,
  type Receipt,
  type StructureOutcome,
  type ValidationIssue,
} from '@bill/core';
import { encodeForUpload } from './image.js';

/**
 * M2 accuracy probe — not the product.
 *
 * Upload a real receipt and see exactly what the model returned and whether it
 * reconciles. This is the "can a cloud model actually read my bills?" question,
 * and it is worth answering before any real UI exists: if the answer is no,
 * nothing downstream matters.
 */

const provider = new HttpProvider(
  import.meta.env.VITE_APP_TOKEN
    ? { token: import.meta.env.VITE_APP_TOKEN as string }
    : {},
);

function IssueList({ issues }: { issues: ValidationIssue[] }): React.ReactElement | null {
  if (issues.length === 0) {
    return <p className="ok">✓ Arithmetic reconciles — no issues found.</p>;
  }
  return (
    <ul className="issues">
      {issues.map((issue, i) => (
        <li key={i} className={issue.severity}>
          <strong>{issue.severity}</strong> {issue.message}
          {issue.expected !== undefined && (
            <em>
              {' '}
              (expected {issue.expected}, got {issue.actual})
            </em>
          )}
        </li>
      ))}
    </ul>
  );
}

function ReceiptTable({ receipt }: { receipt: Receipt }): React.ReactElement {
  const { currency } = receipt;
  return (
    <>
      <table>
        <thead>
          <tr>
            <th>id</th>
            <th>item</th>
            <th>qty</th>
            <th>unit</th>
            <th>line</th>
            <th>category</th>
          </tr>
        </thead>
        <tbody>
          {receipt.items.map((item) => (
            <tr key={item.id}>
              <td>{item.id}</td>
              <td>{item.name}</td>
              <td className="num">{item.qty}</td>
              <td className="num">{formatMoney(item.unitPrice, currency)}</td>
              <td className="num">{formatMoney(item.lineTotal, currency)}</td>
              <td>{item.category}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <dl className="totals">
        <dt>items</dt>
        <dd>{formatMoney(itemsTotal(receipt), currency)}</dd>
        {receipt.subtotal !== undefined && (
          <>
            <dt>subtotal</dt>
            <dd>{formatMoney(receipt.subtotal, currency)}</dd>
          </>
        )}
        {receipt.tax !== undefined && (
          <>
            <dt>tax</dt>
            <dd>{formatMoney(receipt.tax, currency)}</dd>
          </>
        )}
        {receipt.tip !== undefined && (
          <>
            <dt>tip</dt>
            <dd>{formatMoney(receipt.tip, currency)}</dd>
          </>
        )}
        {receipt.total !== undefined && (
          <>
            <dt>total</dt>
            <dd>
              <strong>{formatMoney(receipt.total, currency)}</strong>
            </dd>
          </>
        )}
      </dl>
    </>
  );
}

export function Probe(): React.ReactElement {
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState<number | null>(null);
  const [uploadKb, setUploadKb] = useState<number | null>(null);
  const [outcome, setOutcome] = useState<StructureOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  async function onFile(file: File | undefined): Promise<void> {
    if (!file) return;
    setBusy(true);
    setError(null);
    setOutcome(null);
    setElapsed(null);
    setPreview(URL.createObjectURL(file));

    const started = performance.now();
    try {
      const image = await encodeForUpload(file);
      setUploadKb(Math.round(image.byteLength / 1024));
      setOutcome(
        await structureReceipt(provider, {
          kind: 'image',
          base64: image.base64,
          mimeType: image.mimeType,
        }),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setElapsed(Math.round(performance.now() - started));
      setBusy(false);
    }
  }

  const receipt =
    outcome?.status === 'ok' || outcome?.status === 'needs-repair' ? outcome.receipt : null;

  return (
    <main className="probe">
      <h1>Receipt probe</h1>
      <p className="lede">
        Upload a real bill. This shows what the model returned and whether the numbers add up —
        the M2 accuracy check, before any real UI exists.
      </p>

      <label className="picker">
        <input
          type="file"
          accept="image/*"
          capture="environment"
          disabled={busy}
          onChange={(event) => void onFile(event.target.files?.[0])}
        />
        <span>{busy ? 'Reading…' : 'Choose or photograph a receipt'}</span>
      </label>

      {(elapsed !== null || uploadKb !== null) && (
        <p className="meta">
          {uploadKb !== null && <>upload {uploadKb} KB · </>}
          {elapsed !== null && <>{elapsed} ms round trip</>}
          {outcome && <> · {outcome.attempts} attempt(s)</>}
          {outcome && <> · status {outcome.status}</>}
        </p>
      )}

      {error && <p className="error">{error}</p>}

      {outcome?.status === 'failed' && (
        <div className="error">
          <p>Could not read the receipt.</p>
          <ul>
            {outcome.errors.map((message, i) => (
              <li key={i}>{message}</li>
            ))}
          </ul>
        </div>
      )}

      {receipt && (
        <>
          <h2>{receipt.merchant ?? 'Parsed receipt'}</h2>
          <ReceiptTable receipt={receipt} />
          <IssueList issues={validateReceipt(receipt)} />
          <details>
            <summary>Raw JSON</summary>
            <pre>{JSON.stringify(receipt, null, 2)}</pre>
          </details>
        </>
      )}

      {preview && (
        <details>
          <summary>Photo</summary>
          <img src={preview} alt="the receipt being read" />
        </details>
      )}
    </main>
  );
}
